import React, { useState } from "react";
import "./VerbatimList.css";
import MinimalistButton from "../components/buttons/MinimalistButton";
import VerbatimModal from "../modals/VerbatimModal";
import { base64ToImage, renderVerbatimTranslation } from '../utils/VerbatimUtils';

const VerbatimList = ({
  verbatims,
  notes,
  slides,
  onAdd,
  searchQuery,
  setSearchQuery,
  selectedLabel,
  labelTypes,
  activeSlide,
}) => {
  const [selectedVerbatim, setSelectedVerbatim] = useState(null);

  const handleAddClick = (verbatim, event) => {
    event.stopPropagation(); // Stop event propagation to prevent modal opening
    const isAlreadyAssigned = notes.some(
      (note) => {
        if (typeof note.content === 'string' && typeof verbatim.verbatim === 'string') {
          return note.content === verbatim.verbatim;
        } else if (Array.isArray(note.content) && Array.isArray(verbatim.verbatim)) {
          const hasText = note.content.some(item => item.type === 'text') &&
                verbatim.verbatim.some(item => item.type === 'text');

          const hasImage = note.content.some(item => item.type === 'image') &&
                          verbatim.verbatim.some(item => item.type === 'image');

          const textCheck = hasText
            ? note.content.find(item => item.type === 'text')?.content ===
              verbatim.verbatim.find(item => item.type === 'text')?.content
            : true; // on ignore si pas présent

          const imageCheck = hasImage
            ? note.content.find(item => item.type === 'image')?.content ===
              verbatim.verbatim.find(item => item.type === 'image')?.content
            : true; // on ignore si pas présent

          return textCheck && imageCheck;
        } else {
          return false;
        }
      }
    );

    if (activeSlide && !isAlreadyAssigned) {
      onAdd(verbatim);
    }
  };

  const handleVerbatimClick = (verbatim) => {
    setSelectedVerbatim(verbatim);
  };

  const handleCloseModal = () => {
    setSelectedVerbatim(null);
  };

  const getSlideTitle = (verbatim) => {
    const note = notes.find((note) => {
      if (typeof note.content === 'string' && typeof verbatim.verbatim === 'string') {
        return note.content === verbatim.verbatim;
      } else if (Array.isArray(note.content) && Array.isArray(verbatim.verbatim)) {
        const hasText = note.content.some(item => item.type === 'text') &&
          verbatim.verbatim.some(item => item.type === 'text');

        const hasImage = note.content.some(item => item.type === 'image') &&
          verbatim.verbatim.some(item => item.type === 'image');

        const textCheck = hasText
          ? note.content.find(item => item.type === 'text')?.content ===
            verbatim.verbatim.find(item => item.type === 'text')?.content
          : true; // on ignore si pas présent

        const imageCheck = hasImage
          ? note.content.find(item => item.type === 'image')?.content ===
            verbatim.verbatim.find(item => item.type === 'image')?.content
          : true; // on ignore si pas présent

        return textCheck && imageCheck;
      } else {
        return false;
      }
    });

    if (note) {
      const slide = slides.find((slide) => slide.id === note.slideId);
      return slide ? slide.title : "";
    }

    return "";
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) {
      return text || ""; // Return empty string if text is null or undefined
    }
    return text.slice(0, maxLength) + "...";
  };

  const isAssigned = (verbatim) => {
    const note = notes.find((note) => {
      if (typeof note.content === 'string' && typeof verbatim.verbatim === 'string') {
        return note.content === verbatim.verbatim;
      } else if (Array.isArray(note.content) && Array.isArray(verbatim.verbatim)) {
        const hasText = note.content.some(item => item.type === 'text') &&
          verbatim.verbatim.some(item => item.type === 'text');

        const hasImage = note.content.some(item => item.type === 'image') &&
          verbatim.verbatim.some(item => item.type === 'image');

        const textCheck = hasText
          ? note.content.find(item => item.type === 'text')?.content ===
            verbatim.verbatim.find(item => item.type === 'text')?.content
          : true; // on ignore si pas présent

        const imageCheck = hasImage
          ? note.content.find(item => item.type === 'image')?.content ===
            verbatim.verbatim.find(item => item.type === 'image')?.content
          : true; // on ignore si pas présent

        return textCheck && imageCheck;
      } else {
        return false;
      }
    });

    if (note) {
      const slide = slides.find((slide) => slide.id === note.slideId);
      return slide !== undefined;
    }
    
    return false;
  };

  const renderVerbatimContent = (verbatim) => {
    if (typeof verbatim.verbatim === 'string') {
      return (
        <>
          <span>{truncateText(verbatim.verbatim, 100)}</span>
          {renderVerbatimTranslation(verbatim.translation)}
        </>
      );
    }

    if (Array.isArray(verbatim.verbatim)) {
      return <div className="verbatim-content-container">
        {verbatim.verbatim.map((item, index) => {
          if (item.type === 'text' && item.content.trim() !== '') {
            return <span key={index}>{item.content}</span>;
          } else if (item.type === 'image' && item.content) {
            return (
              <div key={index} className="verbatim-image-thumbnail">
                <img 
                  src={base64ToImage(item.content)} 
                  alt="Verbatim" 
                  className="review-verbatim-image"
                  onError={(e) => {
                    console.error("Erreur de chargement de l'image", e);
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                  }}
                />
              </div>
            );
          }
          return null;
        })}
        {renderVerbatimTranslation(verbatim.translation)}
      </div>;
    }

    return null;
  };

  const filteredVerbatims = Array.isArray(verbatims)
    ? verbatims.filter((verbatim) => {
        // Ensure verbatim text and searchQuery are valid
        let matchesSearchQuery = true;

        if (typeof searchQuery === "string") {
          const searchLower = searchQuery.toLowerCase();
          if (typeof verbatim.verbatim === "string") {
            matchesSearchQuery = verbatim.verbatim
              .toLowerCase()
              .includes(searchLower);
          } else if (Array.isArray(verbatim.verbatim)) {
            matchesSearchQuery = verbatim.verbatim[0].content
              .toLowerCase()
              .includes(searchLower);
          }
        }

        // Ensure labels object and selectedLabel are valid
        const matchesLabel =
          selectedLabel &&
          verbatim.labels &&
          typeof verbatim.labels === "object"
            ? Object.entries(verbatim.labels).some(([key, label]) => {
                if (["Gender", "Age", "Sentiment"].includes(key)) {
                  return label && label.state === selectedLabel;
                }
                return key === selectedLabel;
              })
            : true;

        return matchesSearchQuery && matchesLabel;
      })
    : [];

  return (
    <div className="verbatim-list">
      <input
        className="verbatim-search-bar"
        type="text"
        placeholder="Search verbatims"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: "10px", width: "100%" }}
      />
      <ul>
        {filteredVerbatims.map((verbatim) => {
          const assigned = isAssigned(verbatim);
          const slideTitle = getSlideTitle(verbatim);
          return (
            <li
              key={verbatim.id}
              className={`verbatim-item ${assigned ? "assigned" : ""}`}
              onClick={() => handleVerbatimClick(verbatim)}
            >
              {renderVerbatimContent(verbatim)}
              {assigned && (
                <div className="slide-title">
                  {truncateText(slideTitle, 20)}
                </div>
              )}
              {!assigned && (
                <MinimalistButton onClick={(e) => handleAddClick(verbatim, e)}>
                  +
                </MinimalistButton>
              )}
            </li>
          );
        })}
      </ul>
      {selectedVerbatim && (
        <VerbatimModal
          verbatim={selectedVerbatim}
          onClose={handleCloseModal}
          labelTypes={labelTypes}
        />
      )}
    </div>
  );
};

export default VerbatimList;
